import { FC, FormEvent, useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  CaixaEmployee,
  ensureOperatorForEmployee,
  fetchEmployees,
  openSession,
} from "../utils/caixa.api";
import { getNowDateTimeBr } from "../utils/caixa.utils";
import { getLoggedUserIdentity } from "../../../utils/loggedUser";

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  cancelLabel?: string;
}

export const AberturaCaixaForm: FC<Props> = ({
  onSuccess,
  onCancel,
  cancelLabel = "Cancelar",
}) => {
  const [loading, setLoading] = useState(false);
  const [operatorEmployee, setOperatorEmployee] = useState<CaixaEmployee | null>(
    null,
  );
  const [openingFund, setOpeningFund] = useState("0,00");
  const [notes, setNotes] = useState("");
  const [openedAt] = useState(() => getNowDateTimeBr());

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchEmployees(), getLoggedUserIdentity()])
      .then(([employees, logged]) => {
        if (cancelled) return;
        if (!logged) {
          setOperatorEmployee(null);
          return;
        }
        const matched =
          employees.find((e) => e._id === logged.id) ||
          employees.find(
            (e) => e.name.trim().toLowerCase() === logged.name.trim().toLowerCase(),
          );
        setOperatorEmployee(
          matched || {
            _id: logged.id,
            name: logged.name,
            login: "",
          },
        );
      })
      .catch(() => {
        if (!cancelled) {
          setOperatorEmployee(null);
          Swal.fire(
            "Erro",
            "Não foi possível identificar o operador logado.",
            "error",
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!operatorEmployee) {
      Swal.fire(
        "Atenção",
        "Não foi possível identificar o operador logado. Faça login novamente.",
        "warning",
      );
      return;
    }
    const fund = Number(openingFund.replace(/\./g, "").replace(",", ".")) || 0;
    setLoading(true);
    try {
      const operator = await ensureOperatorForEmployee(operatorEmployee);
      await openSession({
        _id_operator: operator._id,
        opening_fund: fund,
        notes: notes || `Abertura em ${openedAt}`,
      });
      Swal.fire("Sucesso", "Caixa aberto com sucesso!", "success");
      onSuccess();
    } catch {
      Swal.fire("Erro", "Não foi possível abrir o caixa.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-md-6 mb-6">
          <label className="form-label required fw-bold fs-6" htmlFor="abertura-operador">
            Operador
          </label>
          <input
            id="abertura-operador"
            type="text"
            className="form-control form-control-lg form-control-solid"
            value={operatorEmployee?.name || "Carregando..."}
            readOnly
            disabled
          />
        </div>
        <div className="col-md-6 mb-6">
          <label className="form-label required fw-bold fs-6" htmlFor="abertura-data-hora">
            Data e hora
          </label>
          <input
            id="abertura-data-hora"
            type="text"
            className="form-control form-control-lg form-control-solid"
            value={openedAt}
            readOnly
          />
        </div>
        <div className="col-md-6 mb-6">
          <label className="form-label required fw-bold fs-6">Fundo de Caixa (R$)</label>
          <input
            type="text"
            className="form-control form-control-lg form-control-solid"
            value={openingFund}
            onChange={(e) => setOpeningFund(e.target.value)}
            placeholder="0,00"
            required
          />
        </div>
        <div className="col-12 mb-6">
          <label className="form-label fw-bold fs-6">Observações</label>
          <input
            type="text"
            className="form-control form-control-lg form-control-solid"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Informações sobre a abertura do turno"
          />
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-light btn-active-light-primary me-2"
          onClick={onCancel}
        >
          {cancelLabel}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || !operatorEmployee}
        >
          {loading ? "Abrindo..." : "Abrir Caixa"}
        </button>
      </div>
    </form>
  );
};
